import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Alert,
  Button,
  Group,
  Card,
  Image,
  Loader,
  Stack,
} from "@mantine/core";
import integrationService from "../../services/integration.service";
import { BaseResponse, FormDataResponse } from "types/responses.type";
import DynamicForm from "../../components/DynamicForm";

const IntegrationOptions = () => {
  const { integrationName } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<
    "loading" | "success" | "error" | "form"
  >("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState<FormDataResponse>();

  const hasRunCallback = useRef(false);

  const handleResponse = async (response: BaseResponse) => {
    try {
      if (response.ok) {
        switch (response.data?.type ?? "success") {
          case "form":
            setFormData(response.data);
            setStatus("form");
            break;
          case "loading":
            setStatus("loading");
            break;
          case "redirect":
            window.location.href = response.data.redirectUrl;
            break;
          default:
            setStatus("success");
            break;
        }
      } else {
        setStatus("error");
        setErrorMessage(response.errorMessage || "Unknown error occurred.");
      }
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(error.message || "An unexpected error occurred.");
    }
  };

  const handleFormSubmit = async (endpoint: string, data: any) => {
    if (data) {
      try {
        const response = await integrationService.post(endpoint, {}, data);
        handleResponse(response);
      } catch (error: any) {
        setStatus("error");
        setErrorMessage(error.message || "An unexpected error occurred.");
      }
    }
  };

  const handleCallback = async () => {
    const query = Object.fromEntries(searchParams.entries());
    const response = await integrationService.get(
      `/${integrationName!}/options`,
      query
    );
    handleResponse(response);
    hasRunCallback.current = false;
  };

  useEffect(() => {
    if (integrationName && !hasRunCallback.current) {
      hasRunCallback.current = true;
      handleCallback();
    }
  }, [integrationName, searchParams]);

  return (
    <Container size="sm" py="xl">
      {status === "loading" && (
        <Group>
          <Loader size="xs" c="dimmed" />
          <Text c="dimmed">Loading ...</Text>
        </Group>
      )}
      {status === "success" && (
        <Alert title="Success" color="green">
          Integration successfully connected!
        </Alert>
      )}
      {status === "error" && (
        <Alert title="Error" color="red">
          {errorMessage}
        </Alert>
      )}
      {status === "form" && formData && (
        <DynamicForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleFormSubmit}
        />
      )}

      <Text mt="lg">
        <Link to="/settings/integrations">Return to Integrations</Link>
      </Text>
    </Container>
  );
};

export default IntegrationOptions;
